import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MovieCard from "@/components/feature/MovieCard";
import SectionTitle from "@/components/feature/SectionTitle";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getMovieById, getRelatedMovies } from "@/lib/content";

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);

  useDocumentMeta({
    title: movie ? `【${movie.title}】详情介绍 - 国语对白做受69` : "影视资源不存在 - 国语对白做受69",
    description: movie
      ? `${movie.title}（${movie.year}）是一部${movie.region}${movie.genres.join("、")}作品，由${movie.director}执导，${movie.actors.join("、")}主演。${movie.summary.slice(0, 60)}`
      : "抱歉，你访问的影视资源不存在或已下架，请返回首页浏览其他内容。",
    keywords: movie ? `${movie.title},${movie.genres.join(",")},影视详情,国语对白做受69` : "影视资源不存在",
  });

  if (!movie) {
    return (
      <Layout>
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-24 text-center">
          <span className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-film-line text-4xl"></i>
          </span>
          <h1 className="mt-6 text-xl md:text-2xl font-bold text-foreground-900 font-heading">
            影视资源不存在或已下架
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            你访问的作品可能已被移除，或链接有误。返回首页继续浏览其他影视内容吧。
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <Link
              to="/"
              className="px-5 py-2.5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
            <Link
              to="/category/all"
              className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览全部资源
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const typeLabel = movie.type === "movie" ? "电影" : "电视剧";

  return (
    <Layout>
      <div className="w-full bg-primary-950">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-5">
          <nav className="text-xs md:text-sm text-background-400 flex items-center gap-1.5 flex-wrap">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to={`/category/${movie.type}`}
              className="hover:text-accent-400 transition-colors cursor-pointer"
            >
              {typeLabel}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-accent-400">{movie.title}</span>
          </nav>
        </div>
      </div>

      <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 md:gap-10">
          <div className="mx-auto w-52 sm:w-64 lg:w-full">
            <div className="rounded-lg overflow-hidden border border-background-200 bg-background-100">
              <img
                src={movie.poster}
                alt={`${movie.title} 海报`}
                title={`${movie.title} - ${movie.genres.join(" ")}`}
                className="w-full aspect-[2/3] object-cover object-top"
              />
            </div>
          </div>

          <div>
            <div className="flex items-start gap-3 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">{movie.title}</h1>
              <span className="mt-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs font-medium">
                <i className="ri-film-line"></i>
                {typeLabel}
              </span>
            </div>

            <div className="mt-4 flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-1.5 text-accent-600 font-bold text-xl">
                <i className="ri-star-fill"></i>
                {movie.rating.toFixed(1)}
              </span>
              <span className="text-sm text-foreground-500">{movie.year}</span>
              <span className="text-sm text-foreground-500">{movie.region}</span>
              <span className="text-sm text-foreground-500">{movie.duration}</span>
              <span className="text-sm text-foreground-500">热度 {movie.popularity}</span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {movie.genres.map((g) => (
                <span key={g} className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs">
                  {g}
                </span>
              ))}
            </div>

            <dl className="mt-6 rounded-lg border border-background-200 bg-background-50 divide-y divide-background-200 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 p-4">
                <dt className="text-sm text-foreground-500 sm:w-20 shrink-0">导演</dt>
                <dd className="text-sm text-foreground-900">{movie.director}</dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 p-4">
                <dt className="text-sm text-foreground-500 sm:w-20 shrink-0">主演</dt>
                <dd className="text-sm text-foreground-900">{movie.actors.join("、")}</dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 p-4">
                <dt className="text-sm text-foreground-500 sm:w-20 shrink-0">类型</dt>
                <dd className="text-sm text-foreground-900">{movie.genres.join(" / ")}</dd>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 p-4">
                <dt className="text-sm text-foreground-500 sm:w-20 shrink-0">地区 / 年份</dt>
                <dd className="text-sm text-foreground-900">
                  {movie.region} / {movie.year}
                </dd>
              </div>
            </dl>

            <div className="mt-6">
              <h2 className="text-base font-semibold text-foreground-950 mb-2">剧情简介</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{movie.summary}</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/category/${movie.type}`}
                className="px-5 py-2.5 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                更多{typeLabel}
              </Link>
              <Link
                to="/news"
                className="px-5 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-accent-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                相关资讯
              </Link>
            </div>
          </div>
        </div>

        <section className="mt-12 md:mt-16">
          <SectionTitle
            icon="ri-movie-2-line"
            title="相关推荐"
            subtitle="同类型或同主创的影视作品（静态关联演示）"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to={`/category/${movie.type}`}
            className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-accent-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回{typeLabel}分类
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    </Layout>
  );
}